import { useIntl } from 'react-intl';

import { useRequiredParams, useSelector, useTabsState } from '_common/hooks';
import { navigateToMyFiles } from 'router/navigation';

import { useGetRoleQuery } from '../TenantSettingsPage/Roles/RolesApi';

import { ErrorView, IntlErrorBoundary, DocumentTitle } from '_common/components';
import { Tabs } from '@ets/design-system';

import RoleGeneralTab from 'Settings/pages/RoleSettingsPage/RoleGeneralTab/RoleGeneralTab';
import RoleObjectPermissionsTab from 'Settings/pages/RoleSettingsPage/RoleObjectPermissionsTab/RoleObjectPermissionsTab';

import styles from './RoleSettingsPage.module.scss';
import Header from './Header';

const RoleSettingsPage = () => {
  const intl = useIntl();
  const { id: objectId } = useRequiredParams<RouterParams['roleSettings']>();

  const { data: role } = useGetRoleQuery(objectId);
  const error = useSelector((state) => state.app.error);
  const { tab, onTabChange } = useTabsState('roleSettings');

  const checkErrorStatus = () => {
    return error.status === 400 || error.status === 403 || error.status === 404;
  };

  // check for forbidden view
  if (checkErrorStatus()) {
    return <ErrorView error={error} onClick={navigateToMyFiles} />;
  }

  if (!role) {
    return <div />;
  }

  return (
    <DocumentTitle title={`doDOC - ${role.name}`}>
      <div className={styles.root}>
        <div className={styles.header}>
          <IntlErrorBoundary size="s" style={{ margin: '0 0 0 7rem' }}>
            <Header roleName={role.name} />
          </IntlErrorBoundary>
        </div>
        <div className={styles.page}>
          <IntlErrorBoundary size="xl" style={{ margin: '13.5rem 0 0 0' }}>
            <Tabs value={tab || 'general'} onValueChange={onTabChange}>
              <Tabs.List>
                <Tabs.Trigger value="general">
                  {intl.formatMessage({ id: 'settings.tabs.general' })}
                </Tabs.Trigger>
                <Tabs.Trigger value="objectPermissions">
                  {intl.formatMessage({ id: 'settings.roles.objectPermissions' })}
                </Tabs.Trigger>
              </Tabs.List>
              <Tabs.Content value="general">
                <IntlErrorBoundary key="general" size="xl" style={{ margin: '5rem 0' }}>
                  <RoleGeneralTab role={role} />
                </IntlErrorBoundary>
              </Tabs.Content>
              <Tabs.Content value="objectPermissions">
                <IntlErrorBoundary key="objectPermissions" size="xl" style={{ margin: '5rem 0' }}>
                  <RoleObjectPermissionsTab role={role} />
                </IntlErrorBoundary>
              </Tabs.Content>
            </Tabs>
          </IntlErrorBoundary>
        </div>
      </div>
    </DocumentTitle>
  );
};

export default RoleSettingsPage;
